// Packages
import React, { useCallback } from "react"
import PropTypes from "prop-types"
import Slider from "react-slick"
import classNames from "classnames"

// Configs
import ModalConfig from "components/Stacker/config/ModalConfig"
import IPC from "components/Stacker/config/ImagePresetsConfig"
// import ToastConfig from 'config/ToastConfig';

// Actions
import { toggleModal } from "components/Stacker/actions/ModalActions"

// Components
// import Toast from 'components/common/Toast/Toast';
import BgVideo from "components/Stacker/components/BgVideo/BgVideo"

import "./ProductDetailsImages.scss"
import { useState, useRef } from "react"
import { useDispatch } from "react-redux"
import { useI18nContext } from "i18n/i18n-react"

function ProductDetailsImages({
  productImages,
  showThumbs,
  isZoomEnabled,
  isQuickView,
  configProductSet,
  isProductConfigurable,
  productId,
  productName,
  cropQuadrant,
  isMobile,
  image360DegreeUrl,
  image360DegreeCount,
}) {
  const dispatch = useDispatch()
  const { LL } = useI18nContext()
  const [imageUrl, setImageUrl] = useState("")
  const [initialDragPoint, setInitialDragPoint] = useState(1)
  const images360Urls = useRef([])
  const [autoPlayArray, set_autoPlayArray] = useState([])
  const [autoPlayId, set_autoPlayId] = useState(null)
  const hitBox = useRef()
  const image360Ref = useRef()
  const label360Ref = useRef()

  const onMouseDown = useCallback(
    (e) => {
      const closeDragElement = (event) => {
        event.preventDefault()
        document.onmouseup = null
        document.onmousemove = null
      }

      const rotateImage = (e) => {
        e.preventDefault()
        const event = e || window.event
        const currentDragPoint = event.clientX
        let diff = (currentDragPoint - initialDragPoint) / 10
        diff %= image360DegreeCount - 1
        if (diff < 0) {
          diff = image360DegreeCount + diff
        }
        if (images360Urls.current.indexOf(imageUrl) !== Math.round(diff)) {
          setImageUrl(images360Urls.current[Math.round(diff)])
        }
        label360Ref.current.style = "opacity: 0"
      }

      const stopAutoplay = () => {
        if (autoPlayId) {
          clearInterval(autoPlayId)
          set_autoPlayId(null)
        }
        autoPlayArray.forEach((id) => {
          clearTimeout(id)
        })
      }

      const event = e || window.event
      event.preventDefault()
      event.stopPropagation()
      stopAutoplay()
      setInitialDragPoint(event.clientX)
      document.onmousemove = rotateImage
      document.onmouseup = closeDragElement
    },
    [autoPlayArray, autoPlayId, image360DegreeCount, imageUrl, initialDragPoint]
  )

  const openZoomView = useCallback(
    (e) => {
      // firing event when pdp image is clicked

      const autoPlay360Images = () => {
        const playArray = []
        images360Urls.current.forEach((image, i) => {
          playArray.push(
            setTimeout(() => {
              setImageUrl(images360Urls.current[i])
            }, i * 100)
          )
        })
        set_autoPlayArray(playArray)
      }

      if (e.target.classList.contains("no-zoom")) {
        return
      }
      if (e.target.classList.contains("img-fluid")) {
        const { index } = e.target.dataset

        if (e.target.classList.contains("auto-play") && !autoPlayId) {
          autoPlay360Images()
          set_autoPlayId(
            setInterval(autoPlay360Images, images360Urls.current.length * 101)
          )
        }
        return
      }
      dispatch(toggleModal(ModalConfig.MODAL_PDP_ZOOM, true))
    },
    [autoPlayId, dispatch]
  )

  const create360ImagesArray = (imageUrl, count) => {
    const images = []
    const identifier = "360PDP_"
    const strArr = imageUrl.split(identifier)
    for (let i = count; i >= 1; i--) {
      images.push(
        // https://stackoverflow.com/questions/5774042/format-a-number-exactly-two-in-length
        `${strArr[0]}${identifier}${String(`0${i}`).slice(-2)}?$360_view$`
      )
    }
    images360Urls.current = images
  }

  if (image360DegreeUrl) {
    create360ImagesArray(image360DegreeUrl, image360DegreeCount)
  }

  const enableZoomHitbox = () => {
    hitBox.current.style = "display: block;"
  }

  const disableZoomHitbox = () => {
    hitBox.current.style = "display: none;"
  }

  const settings = {
    dots: true,
    dotsClass: showThumbs
      ? "slick-dots slick-thumb d-print-none"
      : "slick-dots d-print-none",
    accessibility: true,
    infinite: !image360DegreeUrl,
    slidesToShow: 1,
    slidesToScroll: 1,
    slickGoTo: 0,
    speed: 500,
    arrows: false,
    afterChange: (index) => {
      // dispatch(updateSlide(index));
      if (image360DegreeUrl) {
        label360Ref.current.style = ""
        disableZoomHitbox()
      } else {
        enableZoomHitbox()
      }
    },
  }

  // set pinky ring hardcoded video values
  let pdpImageSet
  if (isProductConfigurable && productId) {
    // Set theme for PDP videos

    const pdpPinkyVideo = configProductSet.filter(
      (product) => productId === product.PRODUCT_ID && product.BG_VIDEO
    )
    if (pdpPinkyVideo.length) {
      pdpImageSet = [...[pdpPinkyVideo[0].BG_VIDEO], ...productImages]
    } else {
      pdpImageSet = [...productImages]
    }
  } else {
    pdpImageSet = [...productImages]
  }

  if (showThumbs) {
    let thumbPreset = IPC.DEFUALT_PRESET
    if (cropQuadrant) {
      // thumbPreset = `$${cropQuadrant}${IPC.PDP_THUMB}$`
      thumbPreset = `.jpg${cropQuadrant}`
    }
    settings.customPaging = (i) => {
      if (image360DegreeUrl && i === pdpImageSet.length) {
        return (
          <button className="img-fluid auto-play" data-index={i}>
            <img
              className="img-fluid auto-play"
              //	src={`${AEM_ASSETS}/${pdpImageSet[0]}`} // pdpImageSet[0]
              src={`${pdpImageSet[0]}${thumbPreset}`} // `${AEM_ASSETS}/${pdpImageSet[0]}`
              data-index={i}
              alt={`${productName} ${LL.pdp.pdpImageText()}`}
            />
            <span className="img-text img-fluid" data-index={i}>
              360º
            </span>
          </button>
        )
      }
      const srcVal =
        isProductConfigurable && i === 0
          ? `${pdpImageSet[i].poster}`
          : `${pdpImageSet[i]}`
      return (
        <button className="img-fluid" data-index={i}>
          <img
            className="img-fluid"
            //	src={`${AEM_ASSETS}/${pdpImageSet[i]}`} // pdpImageSet[i]
            src={`${srcVal}${thumbPreset}`} // `${AEM_ASSETS}/${pdpImageSet[i]}`
            alt={`${productName} ${LL.pdp.pdpImageText()}`}
            data-index={i}
          />
        </button>
      )
    }
  }

  const shouldZoom =
    (isZoomEnabled === true || isZoomEnabled === "true") && !isQuickView

  //	By default keeping the preset as mktg so that no products Image rendering breaks
  let imagePreset = IPC.DEFUALT_PRESET
  if (cropQuadrant) {
    const pdpPageSuffix = isMobile ? IPC.PDP_MOBILE : IPC.PDP_DESKTOP
    const finalPrefiSuffix = isQuickView ? IPC.QV_DESKTOP : pdpPageSuffix
    imagePreset = `.jpg${cropQuadrant}`
  }

  return (
    <div
      className={classNames("ProductDetailsImages", {
        "zoom-enabled": shouldZoom,
      })}
      aria-label={productName}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <button
        className="hitbox"
        ref={hitBox}
        onClick={shouldZoom ? openZoomView : null}
      />
      <Slider {...settings}>
        {pdpImageSet.map((imagePath, index) => {
          let imgTmp
          if (isProductConfigurable && index === 0) {
            imgTmp = (
              <div key={`${imagePath}-${index}`} className="product-image">
                <BgVideo
                  autoPlay
                  controls
                  muted
                  loop
                  dataAcct={`${imagePath.dataAccount}`}
                  vidId={`${imagePath.vidId}`}
                  poster={`${imagePath.poster}${imagePreset}`}
                />
              </div>
            )
          } else {
            imgTmp = (
              <button
                key={`${imagePath}-${index}`}
                className="product-image"
                tabIndex="-1"
                onClick={shouldZoom && isMobile ? openZoomView : null}
              >
                <img // keep the below Code for local testing if needed.
                  //	src={`${AEM_ASSETS}/${imagePath}`} // imagePath
                  src={`${imagePath}${imagePreset}`} // `${AEM_ASSETS}/${imagePath}`
                  alt="test"
                  aria-hidden="true"
                />
              </button>
            )
          }
          return imgTmp
        })}
        {image360DegreeUrl && (
          /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
          <button className="product-image image-360 no-zoom" ref={image360Ref}>
            <img // keep the below Code for local testing if needed.
              //	src={`${AEM_ASSETS}/${imagePath}`}
              // src={`${imagePath}${imagePreset}`}
              src={imageUrl || images360Urls.current[0]}
              draggable="false"
              alt={`${productName} ${LL.pdp.pdpImageText()}`}
              className="no-zoom"
              onMouseDown={onMouseDown}
            />
            <span className="label-360" id="label-360" ref={label360Ref}>
              Click & Drag to Rotate
            </span>
          </button>
        )}
      </Slider>

      {images360Urls.current.map((imagePath, index) => (
        <img
          src={imagePath}
          alt={`${productName} ${LL.pdp.pdpImageText()}`}
          key={`${imagePath}-${index}`}
          className="hidden-image hide"
        />
      ))}
    </div>
  )
}

ProductDetailsImages.propTypes = {
  productImages: PropTypes.array,
  showThumbs: PropTypes.bool,
  isZoomEnabled: PropTypes.bool, // change once AEM has it as Boolean
  isQuickView: PropTypes.bool,
  zoomToastState: PropTypes.bool,
  isProductConfigurable: PropTypes.bool,
  pinkyProductImages: PropTypes.array,
  timeStamp: PropTypes.string,
  productId: PropTypes.string,
  productName: PropTypes.string,
  cropQuadrant: PropTypes.string,
  isMobile: PropTypes.bool,
  image360DegreeCount: PropTypes.number,
  image360DegreeUrl: PropTypes.string,
  productTheme: PropTypes.string,
  configProductSet: PropTypes.array,
}

export default ProductDetailsImages
